<template>
  <Teleport to="body">
    <Transition name="slide-up">
      <div
        v-if="showBanner"
        class="fixed bottom-0 inset-x-0 z-9999 p-4 md:p-6"
        role="dialog"
        aria-label="Cookie consent"
        aria-modal="false"
      >
        <div class="mx-auto max-w-3xl rounded-xl border border-border bg-background p-6 shadow-2xl">
          <!-- Banner content -->
          <div class="flex flex-col gap-4">
            <div>
              <h2 class="text-lg font-semibold">{{ $t('consent.title') }}</h2>
              <p class="mt-1 text-sm text-muted-foreground">
                {{ $t('consent.description') }}
                <NuxtLink
                  :to="$localeRoute('/legal/privacy-policy')"
                  class="underline hover:text-foreground transition-colors"
                >
                  {{ $t('consent.privacyLink') }}
                </NuxtLink>
                &middot;
                <NuxtLink
                  :to="$localeRoute('/legal/cookie-policy')"
                  class="underline hover:text-foreground transition-colors"
                >
                  {{ $t('consent.cookiePolicyLink') }}
                </NuxtLink>
              </p>
            </div>

            <!-- Quick actions — Accept & Reject have equal visual weight (GDPR compliance) -->
            <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" size="sm" @click="togglePreferences">
                {{ $t('consent.customize') }}
              </Button>
              <Button variant="default" size="sm" @click="handleRejectAll">
                {{ $t('consent.rejectAll') }}
              </Button>
              <Button variant="default" size="sm" @click="handleAcceptAll">
                {{ $t('consent.acceptAll') }}
              </Button>
            </div>
          </div>

          <!-- Expandable preferences panel -->
          <ConsentPreferences
            v-if="preferencesOpen"
            :preferences="state.preferences"
            @save="handleSavePreferences"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from '#imports';
import Button from '#bubba/shared/components/ui/button/Button.vue';
import ConsentPreferences from '#components/consent/ConsentPreferences.vue';
import { useConsentState, type ConsentPreferences as ConsentPreferencesType } from '#bubba/shared/composables/consent/useConsentState';
import { createLogger } from '#bubba/shared/utils/logger';

const log = createLogger('CookieBanner', { enabled: false });

const { $t, $localeRoute } = useI18n();
const { state, showBanner, acceptAll, rejectAll, savePreferences } = useConsentState();

const preferencesOpen = ref(false);

/** Sync anonymous consent choices to backend after banner interaction */
async function syncAnonymousConsents(): Promise<void> {
  try {
    const { useConsentSync } = await import('#composables/useConsentSync');
    const { syncAnonymous } = useConsentSync();
    await syncAnonymous();
  } catch (e: unknown) {
    log.error('Failed to sync anonymous consents after banner interaction', e);
  }
}

function togglePreferences() {
  preferencesOpen.value = !preferencesOpen.value;
}

function handleAcceptAll() {
  acceptAll();
  preferencesOpen.value = false;
  syncAnonymousConsents();
}

function handleRejectAll() {
  rejectAll();
  preferencesOpen.value = false;
  syncAnonymousConsents();
}

function handleSavePreferences(prefs: ConsentPreferencesType) {
  savePreferences(prefs);
  preferencesOpen.value = false;
  syncAnonymousConsents();
}
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
